import {createClient} from '@supabase/supabase-js';
import {debugLog} from './debug';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase environment variables. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY (see .env.example).'
  );
}

/**
 * A small key/value store backed by localStorage.
 *
 * On the desktop app this was `@tauri-apps/plugin-store` writing a JSON file to disk, because
 * Tauri could not use localStorage reliably. On the web localStorage is the natural home, but the
 * async API is preserved verbatim so callers (authThunks) did not have to change.
 */
export interface AppKeyValueStore {
    get<T>(key: string): Promise<T | null>;
    set(key: string, value: unknown): Promise<void>;
    delete(key: string): Promise<void>;
    save(): Promise<void>;
}

const APP_STORE_PREFIX = 'censorme.';

const localStorageStore: AppKeyValueStore = {
  async get<T>(key: string): Promise<T | null> {
    try {
      const raw = localStorage.getItem(APP_STORE_PREFIX + key);
      if (raw === null) {
        debugLog('[Storage] get:', key, 'not found');
        return null;
      }
      debugLog('[Storage] get:', key, 'found');
      return JSON.parse(raw) as T;
    } catch (error) {
      console.error('[Storage] get error:', error);
      return null;
    }
  },
  async set(key: string, value: unknown): Promise<void> {
    try {
      localStorage.setItem(APP_STORE_PREFIX + key, JSON.stringify(value));
      debugLog('[Storage] set:', key, 'saved');
    } catch (error) {
      console.error('[Storage] set error:', error);
    }
  },
  async delete(key: string): Promise<void> {
    try {
      localStorage.removeItem(APP_STORE_PREFIX + key);
      debugLog('[Storage] delete:', key, 'deleted');
    } catch (error) {
      console.error('[Storage] delete error:', error);
    }
  },
    // localStorage writes are already durable; kept so call sites stay unchanged.
  async save(): Promise<void> {},
};

export async function getAuthStore(): Promise<AppKeyValueStore> {
  return localStorageStore;
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
        // Required for the Google OAuth redirect: Supabase reads the code out of the
        // callback URL and exchanges it for a session.
    detectSessionInUrl: true,
    flowType: 'pkce',
    storageKey: 'supabase.auth.token',
    // Guarded only so the build-time prerender (scripts/prerender.mjs) can import this under Node.
    // No auth call is ever made there; in the browser this is always localStorage.
    storage: typeof window === 'undefined' ? undefined : window.localStorage,
  },
});
